const csrfHeaders = (csrfToken) => ({
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  ...(typeof csrfToken === 'string' && csrfToken !== ''
    ? { 'X-CSRF-TOKEN': csrfToken }
    : {}),
})

const abortedError = () => new window.DOMException('The inline image upload was aborted.', 'AbortError')

export default function uploadTemporaryInlineImage(blob, {
  url,
  fileName,
  csrfToken = null,
  onProgress = null,
  signal = null,
}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortedError())

      return
    }

    const request = new window.XMLHttpRequest()
    const form = new window.FormData()
    const abort = () => request.abort()
    let settled = false

    const finish = (callback, value) => {
      if (settled) {
        return
      }

      settled = true
      signal?.removeEventListener('abort', abort)
      callback(value)
    }

    form.append('image', blob, fileName)
    request.open('POST', url)
    request.responseType = 'json'

    Object.entries(csrfHeaders(csrfToken)).forEach(([name, value]) => {
      request.setRequestHeader(name, value)
    })

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.min(100, Math.max(0, Math.round((event.loaded / event.total) * 100))))
      }
    })

    request.addEventListener('load', () => {
      let payload = request.response

      if (payload === null || typeof payload !== 'object') {
        try {
          payload = request.responseText ? JSON.parse(request.responseText) : null
        } catch {
          payload = null
        }
      }

      if (request.status < 200
        || request.status >= 300
        || typeof payload?.token !== 'string'
        || typeof payload?.preview_url !== 'string'
        || typeof payload?.delete_url !== 'string') {
        const error = new Error(payload?.message || `Inline image upload failed (${request.status}).`)
        error.status = request.status
        finish(reject, error)

        return
      }

      finish(resolve, {
        token: payload.token,
        previewUrl: payload.preview_url,
        deleteUrl: payload.delete_url,
      })
    })
    request.addEventListener('error', () => finish(reject, new Error('Inline image upload failed.')))
    request.addEventListener('abort', () => finish(reject, abortedError()))
    signal?.addEventListener('abort', abort, { once: true })
    request.send(form)
  })
}

export async function deleteTemporaryInlineImage(url, csrfToken = null) {
  return await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfHeaders(csrfToken),
  })
}
